import { rgba } from 'polished'
import styled, { keyframes, css } from 'styled-components'

// Muted, consistent status palette (tokens live in src/style.js).
// Age verification uses the brand primary; only "help" gets the amber warning.
const colorsFor = (status, theme) => {
  switch (status) {
    case 'active':
      return { dot: 'var(--color-success)', text: 'var(--color-success)', bg: '#ffffff', border: 'var(--color-line)' }
    case 'help':
      return { dot: 'var(--color-warning)', text: 'var(--color-warning)', bg: 'var(--color-warning-soft)', border: 'rgba(168, 84, 10, 0.35)' }
    case 'age-check':
      return { dot: theme.colors.primary, text: theme.colors.primaryDark, bg: rgba(theme.colors.primary, 0.08), border: rgba(theme.colors.primary, 0.35) }
    default:
      return { dot: 'var(--color-neutral)', text: 'var(--color-muted)', bg: '#ffffff', border: 'var(--color-line)' }
  }
}

export const KiosksBarWrapper = styled.div`
  flex-shrink: 0;
  padding: 12px 24px 16px;
  background: #ffffff;
  border-top: 1px solid var(--color-line);
`

export const KiosksLabel = styled.div`
  margin-bottom: 8px;
  font-size: 13px;
  font-weight: 600;
  line-height: 16px;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: var(--color-muted);
`

// Flex instead of a fixed grid: cards that need an action get more room,
// so status + button stay on one line even at 1536px in Polish.
export const KiosksRow = styled.div`
  display: flex;
  gap: 8px;
`

export const KioskCard = styled.div`
  flex: ${({ $wide }) => ($wide ? 1.6 : 1)} 1 0;
  min-width: 0;
  display: flex;
  align-items: center;
  gap: 8px;
  min-height: 72px;
  padding: 8px 8px 8px 14px;
  border-radius: var(--radius-card);
  border: 1px solid ${({ $status, theme }) => colorsFor($status, theme).border};
  background: ${({ $status, theme }) => colorsFor($status, theme).bg};
  transition: background 0.15s, border-color 0.15s, flex-grow 0.15s;

  @media (max-width: 1600px) {
    padding: 8px 8px 8px 12px;
  }
`

export const KioskInfo = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 2px;
`

export const KioskId = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 16px;
  font-weight: 700;
  line-height: 1.25;
  color: var(--color-text);
  white-space: nowrap;
`

export const KioskStatus = styled.div`
  min-width: 0;
  padding-left: 16px;
  font-size: 14px;
  font-weight: 600;
  line-height: 1.3;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  color: ${({ $status, theme }) => colorsFor($status, theme).text};
`

const pulse = keyframes`
  0%, 100% { opacity: 1; }
  50% { opacity: 0.3; }
`

export const StatusDot = styled.span`
  flex-shrink: 0;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: ${({ $status, theme }) => colorsFor($status, theme).dot};

  ${({ $pulse }) => $pulse && css`
    animation: ${pulse} 1.4s ease-in-out infinite;
  `}
`

export const ActionButton = styled.button`
  flex-shrink: 0;
  height: 56px;
  padding: 0 16px;
  border: none;
  border-radius: var(--radius-control);
  font-size: 15px;
  font-weight: 700;
  font-family: inherit;
  white-space: nowrap;
  color: #ffffff;
  cursor: pointer;
  transition: filter 0.12s, transform 0.12s;

  ${({ $variant, theme }) => $variant === 'age' && css`
    background: ${theme.colors.primaryText || theme.colors.primary};
  `}

  ${({ $variant }) => $variant === 'help' && css`
    background: var(--color-warning);
  `}

  @media (max-width: 1600px) {
    padding: 0 14px;
  }

  &:active {
    filter: brightness(0.9);
    transform: scale(0.97);
  }
`
